import React, { useEffect, useMemo, useState } from "react";
import { api, fmt } from "../../../services/erpApi";
import { StatCard, PageHeader, LoadingScreen, Modal, EmptyState } from "../../../components/admin/EssUi";
import { useToast } from "../../../components/admin/ToastContext";
import { Search, Plus, X, Copy, Ban, CheckCircle2, Eye, RefreshCw, ScrollText, Banknote, CreditCard, AlertTriangle } from "lucide-react";

const PAGE_SIZE = 20;

const moveBadge = (m) => {
  if (m.posted || m.state === "posted") return { text: "#225f2b", bg: "#f0f7f0", label: "Posted" };
  if (m.state === "cancel" || m.state === "cancelled") return { text: "#be123c", bg: "#fff1f2", label: "Cancelled" };
  return { text: "#b45309", bg: "#fffbeb", label: (m.state || "draft").toUpperCase() };
};

const emptyLine = () => ({ account_id: "", partner_id: "", name: "", debit: "", credit: "" });

const AdminPanelAccountMovesPage = () => {
  const toast = useToast();
  const [moves, setMoves] = useState([]);
  const [journals, setJournals] = useState([]);
  const [partners, setPartners] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const [search, setSearch] = useState("");
  const [journalFilter, setJournalFilter] = useState("all");
  const [stateFilter, setStateFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);

  const [showEdit, setShowEdit] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editMove, setEditMove] = useState(null);
  const [form, setForm] = useState({ name: "", move_type: "entry", partner_id: "", journal_id: "", date: new Date().toISOString().slice(0, 10), lines: [emptyLine()] });

  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionId, setActionId] = useState(null);

  useEffect(() => { setPage(1); }, [search, journalFilter, stateFilter, typeFilter, dateFrom, dateTo]);

  const load = async () => {
    try {
      const params = { limit: 500 };
      if (journalFilter !== "all") params.journal_id = journalFilter;
      if (stateFilter !== "all") params.state = stateFilter;
      if (typeFilter !== "all") params.move_type = typeFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const rows = await api.accounting.moves(params);
      setMoves(rows || []);
    } catch { setMoves([]); }
  };

  useEffect(() => {
    (async () => {
      await Promise.all([
        load(),
        api.journals.list({ limit: 500 }).catch(() => []),
        api.partners.list({ limit: 500 }).catch(() => []),
        api.accounts.list({ limit: 500 }).catch(() => []),
      ]).then(([, j, p, a]) => {
        setJournals(j || []);
        setPartners(p || []);
        setAccounts(a || []);
      });
      setLoaded(true);
    })();
  }, []);

  const stats = useMemo(() => {
    const totalDebit = moves.reduce((s, m) => s + (Number(m.total_debit) || 0), 0);
    const totalCredit = moves.reduce((s, m) => s + (Number(m.total_credit) || 0), 0);
    const unbalanced = moves.filter((m) => Math.abs((Number(m.total_debit) || 0) - (Number(m.total_credit) || 0)) > 0.001).length;
    const posted = moves.filter((m) => m.posted || m.state === "posted").length;
    return { count: moves.length, totalDebit, totalCredit, unbalanced, posted };
  }, [moves]);

  const filtered = useMemo(() => {
    return moves.filter((m) => {
      if (search.trim()) {
        const q = search.toLowerCase();
        const hay = `${m.name || ""} ${m.partner_name || ""} ${m.journal_name || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [moves, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const isPosted = (m) => m.posted || m.state === "posted";
  const isCancelled = (m) => m.state === "cancel" || m.state === "cancelled";

  const openDetail = async (m) => {
    setDetail(m);
    setDetailLoading(true);
    try {
      const d = await api.accounting.get(m.id);
      setDetail(d || m);
    } catch { setDetail(m); }
    setDetailLoading(false);
  };

  const handleAction = async (kind, m) => {
    setActionId(m.id);
    try {
      if (kind === "post") {
        await api.accounting.post(m.id);
        toast(`#${m.name || m.id} posted`, "success");
      } else if (kind === "cancel") {
        await api.accounting.cancel(m.id);
        toast(`#${m.name || m.id} cancelled`, "success");
      } else if (kind === "duplicate") {
        const res = await api.accounting.duplicate(m.id);
        toast(res?.state === "draft" ? "Entry duplicated as draft" : "Entry duplicated", "success");
      }
      await load();
    } catch (e) {
      toast(e.message || "Action failed", "error");
    }
    setActionId(null);
  };

  const openEdit = (m) => {
    if (m) {
      setEditMove(m);
      setForm({
        name: m.name || "",
        move_type: m.move_type || "entry",
        partner_id: m.partner_id || "",
        journal_id: m.journal_id || "",
        date: m.date ? String(m.date).slice(0, 10) : new Date().toISOString().slice(0, 10),
        lines: (m.lines || []).length > 0 ? m.lines.map((l) => ({ account_id: l.account_id ?? "", partner_id: l.partner_id ?? "", name: l.name || "", debit: l.debit ?? "", credit: l.credit ?? "" })) : [emptyLine()],
      });
    } else {
      setEditMove(null);
      setForm({ name: "", move_type: "entry", partner_id: "", journal_id: "", date: new Date().toISOString().slice(0, 10), lines: [emptyLine()] });
    }
    setShowEdit(true);
  };

  const lineTotal = (lines) => lines.reduce((s, l) => s + (Number(l.debit) || 0) - (Number(l.credit) || 0), 0);
  const formBalanced = Math.abs(lineTotal(form.lines)) < 0.001 && form.lines.some((l) => l.account_id);

  const handleLineChange = (idx, patch) => {
    setForm((f) => {
      const lines = f.lines.map((l, i) => (i === idx ? { ...l, ...patch } : l));
      return { ...f, lines };
    });
  };

  const handleSave = async () => {
    const validLines = form.lines.filter((l) => l.account_id && ((Number(l.debit) || 0) !== 0 || (Number(l.credit) || 0) !== 0));
    if (validLines.length < 2) { toast("Add at least two lines", "error"); return; }
    if (!formBalanced) { toast("Lines must be balanced (debit = credit)", "error"); return; }
    if (!form.journal_id) { toast("Journal is required", "error"); return; }
    setEditing(true);
    try {
      const body = {
        name: form.name.trim() || undefined,
        move_type: form.move_type,
        partner_id: form.partner_id ? parseInt(form.partner_id) : undefined,
        journal_id: parseInt(form.journal_id),
        date: form.date || undefined,
        state: "draft",
        lines: validLines.map((l) => ({
          account_id: parseInt(l.account_id),
          partner_id: l.partner_id ? parseInt(l.partner_id) : undefined,
          name: l.name || undefined,
          debit: Number(l.debit) || 0,
          credit: Number(l.credit) || 0,
        })),
      };
      if (editMove) {
        await api.accounting.update(editMove.id, body);
        toast("Entry updated", "success");
      } else {
        await api.accounting.create(body);
        toast("Entry created", "success");
      }
      setShowEdit(false);
      await load();
    } catch (e) {
      toast(e.message || "Save failed", "error");
    }
    setEditing(false);
  };

  const inputStyle = {
    width: "100%", padding: "9px 12px", border: "1px solid #cbd5e1", borderRadius: "6px",
    fontSize: "13.5px", outline: "none", background: "#fff", boxSizing: "border-box",
  };
  const labelStyle = { fontSize: "13px", fontWeight: "600", color: "#334155", marginBottom: "6px", display: "block" };
  const FormField = ({ label, children }) => (
    <div style={{ marginBottom: "16px" }}>
      <label style={labelStyle}>{label}</label>
      {children}
    </div>
  );

  if (!loaded) return <LoadingScreen />;

  return (
    <div className="max-w-7xl mx-auto animate-fade-in" style={{ padding: "0 16px" }}>
      <PageHeader
        title="Journal Entries"
        subtitle="Accounting moves — create, post, cancel and duplicate entries"
        actions={
          <button onClick={() => openEdit(null)} className="ess-btn ess-btn-primary">
            <Plus size={16} />
            New Entry
          </button>
        }
      />

      <div className="ess-sg ess-sg4" style={{ marginBottom: "24px" }}>
        <StatCard label="Entries" value={String(stats.count)} icon={ScrollText} trend={`${stats.posted} posted`} />
        <StatCard label="Total Debit" value={fmt.moneyShort(stats.totalDebit)} icon={Banknote} trend={fmt.money(stats.totalDebit)} />
        <StatCard label="Total Credit" value={fmt.moneyShort(stats.totalCredit)} icon={CreditCard} trend={fmt.money(stats.totalCredit)} />
        <StatCard label="Unbalanced" value={String(stats.unbalanced)} icon={AlertTriangle} trend="debit ≠ credit" />
      </div>

      <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginBottom: "24px", background: "white", padding: "16px", borderRadius: "8px", border: "1px solid #e2e7e2" }}>
        <div style={{ flex: 1, minWidth: "200px", position: "relative" }}>
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "#94a3b8" }} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search number, partner, journal…" style={{ ...inputStyle, padding: "9px 12px 9px 36px" }} />
        </div>
        <select value={journalFilter} onChange={(e) => setJournalFilter(e.target.value)} style={{ ...inputStyle, minWidth: "160px", cursor: "pointer" }}>
          <option value="all">All Journals</option>
          {journals.map((j) => <option key={j.id} value={j.id}>{j.name}</option>)}
        </select>
        <select value={stateFilter} onChange={(e) => setStateFilter(e.target.value)} style={{ ...inputStyle, minWidth: "140px", cursor: "pointer" }}>
          <option value="all">All States</option>
          <option value="draft">Draft</option>
          <option value="posted">Posted</option>
          <option value="cancel">Cancelled</option>
        </select>
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} style={{ ...inputStyle, minWidth: "150px", cursor: "pointer" }}>
          <option value="all">All Types</option>
          <option value="entry">Entry</option>
          <option value="out_invoice">Out Invoice</option>
          <option value="in_invoice">In Invoice</option>
        </select>
        <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} style={{ ...inputStyle, minWidth: "150px" }} />
        <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} style={{ ...inputStyle, minWidth: "150px" }} />
      </div>

      <div style={{ background: "white", borderRadius: "8px", border: "1px solid #e2e7e2", overflow: "hidden" }}>
        {paginated.length === 0 ? (
          <EmptyState icon="📜" title="No entries" subtitle="Journal entries will appear here" />
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
              <thead>
                <tr style={{ background: "#fafbfa", borderBottom: "1px solid #e2e7e2" }}>
                  <th className="ess-th" style={{ minWidth: "130px" }}>Number</th>
                  <th className="ess-th" style={{ minWidth: "100px" }}>Date</th>
                  <th className="ess-th" style={{ minWidth: "120px" }}>Journal</th>
                  <th className="ess-th" style={{ minWidth: "140px" }}>Partner</th>
                  <th className="ess-th" style={{ textAlign: "center", minWidth: "80px" }}>Lines</th>
                  <th className="ess-th ess-th-r" style={{ minWidth: "100px" }}>Debit</th>
                  <th className="ess-th ess-th-r" style={{ minWidth: "100px" }}>Credit</th>
                  <th className="ess-th" style={{ minWidth: "90px" }}>State</th>
                  <th className="ess-th ess-th-r" style={{ minWidth: "150px" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginated.map((m, index) => {
                  const b = moveBadge(m);
                  const posted = isPosted(m);
                  const cancelled = isCancelled(m);
                  return (
                    <tr key={m.id} className="ess-tr" style={{ borderBottom: index < paginated.length - 1 ? "1px solid #f1f3f0" : "none" }}>
                      <td className="ess-td" style={{ fontWeight: "600", color: "#1e293b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{m.name || `MOVE ${m.id}`}</td>
                      <td className="ess-td" style={{ color: "#64748b", whiteSpace: "nowrap" }}>{fmt.date(m.date)}</td>
                      <td className="ess-td" style={{ color: "#475569", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{m.journal_name || "—"}</td>
                      <td className="ess-td" style={{ color: "#475569", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{m.partner_name || "—"}</td>
                      <td className="ess-td" style={{ color: "#64748b", textAlign: "center", whiteSpace: "nowrap" }}>{m.line_count ?? "—"}</td>
                      <td className="ess-td" style={{ color: "#225f2b", textAlign: "right", whiteSpace: "nowrap" }}>{fmt.money(m.total_debit)}</td>
                      <td className="ess-td" style={{ color: "#be123c", textAlign: "right", whiteSpace: "nowrap" }}>{fmt.money(m.total_credit)}</td>
                      <td className="ess-td" style={{ whiteSpace: "nowrap" }}>
                        <span style={{ background: b.bg, color: b.text, padding: "3px 10px", borderRadius: "6px", fontSize: "12px", fontWeight: "500", whiteSpace: "nowrap" }}>{b.label}</span>
                      </td>
                      <td className="ess-td" style={{ whiteSpace: "nowrap", textAlign: "right" }}>
                        <div style={{ display: "flex", gap: "4px", justifyContent: "flex-end" }}>
                          <button title="View" onClick={() => openDetail(m)} style={{ border: "1px solid #cbd5e1", background: "white", color: "#475569", cursor: "pointer", padding: "5px 8px", borderRadius: "6px", display: "flex", alignItems: "center" }}><Eye size={13} /></button>
                          {!cancelled && (
                            <>
                              <button title="Duplicate" onClick={() => handleAction("duplicate", m)} disabled={actionId === m.id} style={{ border: "1px solid #cbd5e1", background: "white", color: "#0f766e", cursor: "pointer", padding: "5px 8px", borderRadius: "6px", display: "flex", alignItems: "center" }}><Copy size={13} /></button>
                              {!posted && (
                                <>
                                  <button title="Edit" onClick={() => openEdit(m)} style={{ border: "1px solid #cbd5e1", background: "white", color: "#225f2b", cursor: "pointer", padding: "5px 8px", borderRadius: "6px", display: "flex", alignItems: "center" }}><Plus size={13} /></button>
                                  <button title="Post" onClick={() => handleAction("post", m)} disabled={actionId === m.id} className="ess-btn ess-btn-primary" style={{ padding: "6px 12px", fontSize: "12px" }}>
                                    {actionId === m.id ? <RefreshCw size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
                                    {actionId === m.id ? "…" : "Post"}
                                  </button>
                                </>
                              )}
                              {posted && (
                                <button title="Cancel" onClick={() => handleAction("cancel", m)} disabled={actionId === m.id} style={{ border: "1px solid #cbd5e1", background: "white", color: "#be123c", cursor: "pointer", padding: "5px 8px", borderRadius: "6px", display: "flex", alignItems: "center" }}><Ban size={13} /></button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", borderTop: "1px solid #e2e7e2", background: "#fafbfa" }}>
          <span style={{ fontSize: "13px", color: "#64748b" }}>
            Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}
          </span>
          <div style={{ display: "flex", gap: "4px" }}>
            <button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} style={{ padding: "6px 12px", border: "1px solid #cbd5e1", borderRadius: "6px", background: page <= 1 ? "#f1f5f9" : "white", color: page <= 1 ? "#94a3b8" : "#334155", fontSize: "13px", fontWeight: "500", cursor: page <= 1 ? "not-allowed" : "pointer" }}>← Prev</button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              const start = Math.max(1, Math.min(page - 2, totalPages - 4));
              const p = start + i;
              if (p > totalPages) return null;
              return <button key={p} onClick={() => setPage(p)} style={{ padding: "6px 12px", border: "1px solid #cbd5e1", borderRadius: "6px", background: p === page ? "#225f2b" : "white", color: p === page ? "white" : "#334155", fontSize: "13px", fontWeight: p === page ? "600" : "500", cursor: "pointer" }}>{p}</button>;
            })}
            <button disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} style={{ padding: "6px 12px", border: "1px solid #cbd5e1", borderRadius: "6px", background: page >= totalPages ? "#f1f5f9" : "white", color: page >= totalPages ? "#94a3b8" : "#334155", fontSize: "13px", fontWeight: "500", cursor: page >= totalPages ? "not-allowed" : "pointer" }}>Next →</button>
          </div>
        </div>
      </div>

      <Modal open={showEdit} onClose={() => setShowEdit(false)} title={editMove ? `Edit Entry — ${editMove.name || editMove.id}` : "New Journal Entry"} size="lg">
        <div>
          <div style={{ display: "grid", gap: "12px" }} className="ess-rg-2">
            <FormField label="Journal *">
              <select value={form.journal_id} onChange={(e) => setForm((f) => ({ ...f, journal_id: e.target.value }))} style={inputStyle}>
                <option value="">Select journal…</option>
                {journals.map((j) => <option key={j.id} value={j.id}>{j.name}</option>)}
              </select>
            </FormField>
            <FormField label="Date">
              <input type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} style={inputStyle} />
            </FormField>
          </div>
          <div style={{ display: "grid", gap: "12px" }} className="ess-rg-2">
            <FormField label="Move Type">
              <select value={form.move_type} onChange={(e) => setForm((f) => ({ ...f, move_type: e.target.value }))} style={inputStyle}>
                <option value="entry">Entry</option>
                <option value="out_invoice">Out Invoice</option>
                <option value="in_invoice">In Invoice</option>
                <option value="out_refund">Out Refund</option>
                <option value="in_refund">In Refund</option>
              </select>
            </FormField>
            <FormField label="Partner">
              <select value={form.partner_id} onChange={(e) => setForm((f) => ({ ...f, partner_id: e.target.value }))} style={inputStyle}>
                <option value="">No partner…</option>
                {partners.map((p) => <option key={p.id} value={p.id}>{p.display_name || p.name}</option>)}
              </select>
            </FormField>
          </div>
          <FormField label="Number (optional)">
            <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="INV/2026/00001" style={inputStyle} />
          </FormField>

          <label style={labelStyle}>Lines (debit must equal credit)</label>
          <div style={{ border: "1px solid #e2e7e2", borderRadius: "8px", padding: "12px", marginBottom: "12px" }} className="ess-lines">
            {form.lines.map((line, idx) => (
              <div key={idx} style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1.6fr 80px 80px 32px", gap: "8px", alignItems: "center", marginBottom: "8px" }} className="ess-line">
                <select value={line.account_id || ""} onChange={(e) => handleLineChange(idx, { account_id: e.target.value })} style={inputStyle}>
                  <option value="">Account…</option>
                  {accounts.map((a) => <option key={a.id} value={a.id}>{a.code} — {a.name}</option>)}
                </select>
                <select value={line.partner_id || ""} onChange={(e) => handleLineChange(idx, { partner_id: e.target.value })} style={inputStyle}>
                  <option value="">Partner…</option>
                  {partners.map((p) => <option key={p.id} value={p.id}>{p.display_name || p.name}</option>)}
                </select>
                <input value={line.name || ""} onChange={(e) => handleLineChange(idx, { name: e.target.value })} placeholder="Label" style={inputStyle} />
                <input type="number" min="0" value={line.debit ?? ""} onChange={(e) => handleLineChange(idx, { debit: e.target.value })} placeholder="Debit" style={inputStyle} />
                <input type="number" min="0" value={line.credit ?? ""} onChange={(e) => handleLineChange(idx, { credit: e.target.value })} placeholder="Credit" style={inputStyle} />
                <button onClick={() => setForm((f) => ({ ...f, lines: f.lines.filter((_, i) => i !== idx) }))} disabled={form.lines.length === 1} style={{ background: "transparent", border: "none", color: "#be123c", cursor: form.lines.length === 1 ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <X size={16} />
                </button>
              </div>
            ))}
            <button onClick={() => setForm((f) => ({ ...f, lines: [...f.lines, emptyLine()] }))} className="inline-flex items-center gap-1.5 text-sm font-medium" style={{ color: "#225f2b", background: "none", border: "none", cursor: "pointer", padding: "4px 0" }}>
              <Plus size={16} /> Add line
            </button>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: "20px", marginBottom: "20px" }}>
            <span style={{ fontSize: "13px", color: "#64748b" }}>
              Balance: <strong style={{ color: formBalanced ? "#225f2b" : "#be123c", fontWeight: "700" }}>{formBalanced ? "0 (balanced)" : fmt.money(lineTotal(form.lines))}</strong>
            </span>
          </div>

          <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end", borderTop: "1px solid #e2e7e2", paddingTop: "20px" }}>
            <button onClick={() => setShowEdit(false)} className="ess-btn ess-btn-secondary">Cancel</button>
            <button onClick={handleSave} disabled={editing} className="ess-btn ess-btn-primary">
              {editing ? "Saving…" : editMove ? "Update Entry" : "Create Entry"}
            </button>
          </div>
        </div>
      </Modal>

      <Modal open={!!detail} onClose={() => setDetail(null)} title={detail ? `${detail.name || `MOVE ${detail.id}`}` : ""} size="lg">
        {detail && (
          <div>
            <div style={{ display: "grid", gap: "12px", marginBottom: "16px" }} className="ess-rg-3">
              <div style={{ background: "#fafbfa", border: "1px solid #e2e7e2", borderRadius: "8px", padding: "12px", textAlign: "center" }}>
                <div style={{ fontSize: "16px", fontWeight: "700", color: "#1e293b" }}>{fmt.money(detail.total_debit)}</div>
                <div style={{ fontSize: "11px", color: "#64748b" }}>Debit</div>
              </div>
              <div style={{ background: "#fafbfa", border: "1px solid #e2e7e2", borderRadius: "8px", padding: "12px", textAlign: "center" }}>
                <div style={{ fontSize: "16px", fontWeight: "700", color: "#1e293b" }}>{fmt.money(detail.total_credit)}</div>
                <div style={{ fontSize: "11px", color: "#64748b" }}>Credit</div>
              </div>
              <div style={{ background: "#fafbfa", border: "1px solid #e2e7e2", borderRadius: "8px", padding: "12px", textAlign: "center" }}>
                <div style={{ fontSize: "16px", fontWeight: "700", color: detail.amount_residual ? "#b45309" : "#1e293b" }}>{fmt.money(detail.amount_residual)}</div>
                <div style={{ fontSize: "11px", color: "#64748b" }}>Residual</div>
              </div>
            </div>
            <p style={{ fontSize: "13px", color: "#475569", marginBottom: "12px" }}>
              <strong>Journal:</strong> {detail.journal_name || "—"} · <strong>Date:</strong> {fmt.date(detail.date)} · <strong>Partner:</strong> {detail.partner_name || "—"} · <strong>Type:</strong> {detail.move_type || "—"}
            </p>
            {detailLoading ? (
              <LoadingScreen />
            ) : (detail.lines || []).length === 0 ? (
              <EmptyState icon="📭" title="No lines" subtitle="This entry has no lines" />
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: "#fafbfa", borderBottom: "1px solid #e2e7e2" }}>
                      <th className="ess-th" style={{ fontSize: "11px", padding: "10px 12px" }}>Account</th>
                      <th className="ess-th" style={{ fontSize: "11px", padding: "10px 12px" }}>Label</th>
                      <th className="ess-th ess-th-r" style={{ fontSize: "11px", padding: "10px 12px" }}>Debit</th>
                      <th className="ess-th ess-th-r" style={{ fontSize: "11px", padding: "10px 12px" }}>Credit</th>
                      <th className="ess-th" style={{ fontSize: "11px", padding: "10px 12px", textAlign: "center" }}>Rec.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(detail.lines || []).map((l, index) => (
                      <tr key={index} className="ess-tr" style={{ borderBottom: "1px solid #f1f3f0" }}>
                        <td className="ess-td" style={{ padding: "10px 12px", fontFamily: "monospace", color: "#225f2b", whiteSpace: "nowrap" }}>{l.account_code || l.account_id} — {l.account_name || ""}</td>
                        <td className="ess-td" style={{ padding: "10px 12px", color: "#334155" }}>{l.name || "—"}</td>
                        <td className="ess-td" style={{ padding: "10px 12px", color: "#225f2b", textAlign: "right", whiteSpace: "nowrap" }}>{l.debit ? fmt.money(l.debit) : "—"}</td>
                        <td className="ess-td" style={{ padding: "10px 12px", color: "#be123c", textAlign: "right", whiteSpace: "nowrap" }}>{l.credit ? fmt.money(l.credit) : "—"}</td>
                        <td className="ess-td" style={{ padding: "10px 12px", color: l.reconciled ? "#225f2b" : "#94a3b8", textAlign: "center", fontWeight: "600" }}>{l.reconciled ? "✓" : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AdminPanelAccountMovesPage;